import React, { useMemo, lazy, Suspense, ComponentType } from 'react';
import { Quotation } from '../types';
import { useAccommodationsState } from '@/hooks/useAccommodationsState';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { ErrorBoundary } from '@/components/ui/error-boundary';

// Retry logic for lazy loading with proper typing
const loadWithRetry = <T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retries = 3, 
  delay = 1000
): Promise<{ default: T }> => {
  return new Promise((resolve, reject) => {
    importFn()
      .then(resolve)
      .catch((error) => {
        // If no more retries left, reject
        if (retries <= 0) {
          return reject(error);
        }
        
        // Retry after delay
        setTimeout(() => {
          console.log(`Retrying dynamic import, ${retries} attempts left`);
          loadWithRetry(importFn, retries - 1, delay).then(resolve, reject);
        }, delay);
      });
  });
};

// Lazy load the AccommodationGroupSection component with retry logic
const AccommodationGroupSection = lazy(() => 
  loadWithRetry(() => import('@/components/quotation/tabs/AccommodationGroupSection'))
);

interface AccommodationsTabProps {
  quotation: Quotation;
  isMobile?: boolean;
}

const AccommodationsTab: React.FC<AccommodationsTabProps> = ({ quotation, isMobile = false }) => {
  const {
    accommodationsByPage,
    hasAccommodations,
    activeImageIndices,
    collapsedSections,
    expandedDetails,
    hoveredAccommodation,
    toggleSection,
    toggleDetails,
    handleAccommodationHover,
    setAccommodationActiveImageIndex,
    isUsingCustomGroups
  } = useAccommodationsState(quotation);

  // Memoize the descriptions to prevent unnecessary re-renders
  const accommodationsDescription = useMemo(() => {
    if (!quotation.accommodationsDescription || quotation.accommodationsDescription.trim() === '') {
      return null;
    }
    
    return (
      <div className="mb-6">
        <RichTextEditor 
          value={quotation.accommodationsDescription}
          onChange={() => {}}
          readOnly={true}
          className="border-none shadow-none bg-transparent p-0"
        />
      </div>
    );
  }, [quotation.accommodationsDescription]);

  if (!hasAccommodations) {
    return (
      <div className="p-3 sm:p-6 lg:p-8">
        <div className="bg-gray-100 p-4 rounded-md text-center text-gray-500">
          Não há informações de hospedagem disponíveis para esta cotação.
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 lg:p-8">
      {accommodationsDescription}
      
      <div className="space-y-6 sm:space-y-8">
        <ErrorBoundary
          onReset={() => {
            // Force re-fetching the module by clearing module cache
            window.location.reload();
          }}
        >
          <Suspense fallback={
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin text-voah-teal mr-2" />
              <span>Carregando hospedagens...</span>
            </div>
          }>
            {Object.entries(accommodationsByPage).map(([groupId, { pageTitle, pageDescription, accommodations }]) => (
              <AccommodationGroupSection
                key={groupId}
                groupId={groupId}
                groupTitle={pageTitle}
                groupDescription={pageDescription}
                accommodations={accommodations}
                isCollapsed={collapsedSections[groupId]}
                expandedDetails={expandedDetails}
                activeImageIndices={activeImageIndices}
                hoveredAccommodation={hoveredAccommodation}
                onToggleSection={toggleSection}
                onToggleDetails={toggleDetails}
                onSetActiveImageIndex={setAccommodationActiveImageIndex}
                onHoverAccommodation={handleAccommodationHover}
                showMapSection={quotation.showAccommodationsMapSection !== false}
              />
            ))}
          </Suspense>
        </ErrorBoundary>
      </div>
    </div>
  );
};

export default React.memo(AccommodationsTab); 